// Preis-Eingabe in Euro, Speicherung in Cent (A12.3).
//
// WARUM EINE EIGENE DATEI STATT PARSE-FLOAT IM FELD
//   Der Server nimmt nur ganze Cent und weist alles andere ab — er
//   rundet ausdruecklich NICHT. Was hier falsch umgerechnet wird, kommt
//   also nicht als Fehler zurueck, sondern als falscher Preis. Deshalb
//   liegt die Umrechnung in einer Funktion, die ohne Browser pruefbar
//   ist, und nicht in einem onChange.

/** Was `preisTextZuCent` liefert, wenn der Text kein Preis ist. */
export const PREIS_UNGUELTIG = Symbol('preis-ungueltig');

/**
 * Euro-Text zu Cent.
 *
 * Leer heisst `null` — „kein Preis erfasst", nicht „null Euro". Der
 * Unterschied traegt bis in die Datenbank: NULL steht fuer unbekannt,
 * 0 fuer geschenkt.
 *
 * KOMMA UND PUNKT
 *   Deutsch getippt ist das Komma das Dezimalzeichen und der Punkt
 *   trennt Tausender: „1.234,56" sind 123456 Cent. Wer aber auf einer
 *   Zehnertastatur „12.50" tippt, meint zwoelf Euro fuenfzig und nicht
 *   zwoelfhundertfuenfzig. Die Regel dazwischen:
 *
 *     - Steht ein Komma da, sind alle Punkte Tausendertrenner.
 *     - Steht kein Komma da und genau EIN Punkt mit einer oder zwei
 *       Ziffern dahinter, ist dieser Punkt das Dezimalzeichen.
 *     - Sonst sind Punkte Tausendertrenner.
 *
 *   „12.500" bleibt damit zwoelftausendfuenfhundert — drei Ziffern
 *   hinter dem Punkt sind kein Centbetrag.
 */
export function preisTextZuCent(text: string): number | null | typeof PREIS_UNGUELTIG {
  const roh = text.replace(/\s/g, '').replace(/€/g, '');
  if (roh === '') return null;

  let normiert: string;
  if (roh.includes(',')) {
    normiert = roh.replace(/\./g, '').replace(',', '.');
  } else {
    const punkte = roh.split('.').length - 1;
    const nachkomma = roh.slice(roh.lastIndexOf('.') + 1);
    normiert =
      punkte === 1 && nachkomma.length >= 1 && nachkomma.length <= 2
        ? roh
        : roh.replace(/\./g, '');
  }

  if (!/^\d+(\.\d{1,2})?$/.test(normiert)) return PREIS_UNGUELTIG;

  // Ueber Cent-Zeichenketten rechnen statt ueber Gleitkomma: 19.99 * 100
  // ergibt in JavaScript 1998.9999999999998, und `Math.round` daraufhin
  // zwar 1999 — aber nur, weil es diesmal gutgeht. Die Ziffern selbst
  // luegen nicht.
  const [ganz, bruch = ''] = normiert.split('.');
  return Number(ganz) * 100 + Number((bruch + '00').slice(0, 2));
}

/** Cent zum Eingabewert: 1250 -> „12,50". `null` -> leeres Feld. */
export function centZuEingabe(cent: number | null): string {
  if (cent === null) return '';
  const v = (cent / 100).toFixed(2);
  return v.replace('.', ',');
}

/** Cent zur Anzeige mit Waehrung: 1250 -> „12,50 €". */
export function centAnzeige(cent: number): string {
  return (cent / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}
